/* =============================================================================
 * Copyright (c) 2026 SKYXOPS Corp. All rights reserved.
 *
 * This file is proprietary and confidential. Unauthorized copying,
 * distribution, modification, or use of this file, via any medium,
 * is strictly prohibited without the express written permission of
 * SKYXOPS Corp.
 * ============================================================================= */
import { useState } from 'react';
import {
  Alert, AlertDescription, Button, Card, CardContent, CardDescription, CardHeader, CardTitle,
  Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@skyxops/ui';
import { BadgeCheck } from 'lucide-react';
import { formatDateTime } from '@skyxops/ui';
import { ApiError, updateProfile, type Profile } from '../../api';

const TIME_ZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Los_Angeles',
  'Europe/London', 'Europe/Berlin', 'Asia/Kolkata', 'Asia/Singapore', 'Australia/Sydney',
];
const LOCALES = [{ v: 'en-US', l: 'English (US)' }, { v: 'en-GB', l: 'English (UK)' }, { v: 'es', l: 'Español' }];

function initials(name: string | null, email: string | null): string {
  const base = (name || email || '?').trim();
  const parts = base.split(/[\s@.]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || base[0].toUpperCase();
}

export function IdentitySection({ profile, onSaved }: { profile: Profile; onSaved: (p: Profile) => void }) {
  const [name, setName] = useState(profile.display_name ?? '');
  const [tz, setTz] = useState(profile.time_zone ?? '');
  const [locale, setLocale] = useState(profile.locale ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const dirty = name !== (profile.display_name ?? '') || tz !== (profile.time_zone ?? '') || locale !== (profile.locale ?? '');

  async function onSave() {
    setBusy(true); setError(null); setSavedAt(null);
    try {
      const updated = await updateProfile({ display_name: name, time_zone: tz || undefined, locale: locale || undefined });
      onSaved(updated);
      setSavedAt(formatDateTime(new Date(), { format: 'time' }));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save changes.');
    } finally { setBusy(false); }
  }

  const label = 'text-sm font-medium text-foreground';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile</CardTitle>
        <CardDescription>How you appear across SKYXOPS.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

        <div className="flex items-center gap-4">
          <div className="grid h-16 w-16 place-items-center rounded-full bg-skyxops-midnight text-xl font-semibold text-white">
            {initials(profile.display_name, profile.email)}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor="dn" className={label}>Display name</label>
            <Input id="dn" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} disabled={busy} />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="em" className={label}>Email address</label>
            <div className="relative">
              <Input id="em" value={profile.email ?? ''} disabled className="pr-24 opacity-70" />
              {profile.email_confirmed && (
                <span className="absolute right-2 top-1/2 inline-flex -translate-y-1/2 items-center gap-1 rounded border border-skyxops-beach/40 px-1.5 py-0.5 text-[0.65rem] font-semibold text-skyxops-beach">
                  <BadgeCheck className="h-3 w-3" /> Verified
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {profile.is_federated && profile.sso_provider
                ? `Managed by your ${profile.sso_provider === 'microsoft' ? 'Microsoft' : 'Google'} account — can't be changed here.`
                : 'Managed by your identity provider — contact an admin to change.'}
            </p>
          </div>
          <div className="space-y-1.5">
            <label className={label}>Time zone</label>
            <Select value={tz} onValueChange={setTz}>
              <SelectTrigger><SelectValue placeholder="Select a time zone" /></SelectTrigger>
              <SelectContent>{TIME_ZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className={label}>Language</label>
            <Select value={locale} onValueChange={setLocale}>
              <SelectTrigger><SelectValue placeholder="Select a language" /></SelectTrigger>
              <SelectContent>{LOCALES.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          {savedAt && <span className="text-xs text-muted-foreground">Saved {savedAt}</span>}
          <Button className="bg-skyxops-midnight text-white hover:bg-skyxops-midnight/90"
            onClick={onSave} disabled={busy || !dirty}>
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
